// GIF Library content component for the unified library
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Plus, MoreVertical, Pencil, Trash2, ExternalLink, Images, Gift, Copy } from 'lucide-react'
import { useGifSessions } from '@/hooks/useGifSessions'
import { GifSession } from '@/lib/local-types'
import { cn } from '@/lib/utils'

export function GifLibraryContent() {
  const navigate = useNavigate()
  const { sessions, isLoading, deleteSession, renameSession, duplicateSession, refreshSessions } = useGifSessions()
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [sessionToDelete, setSessionToDelete] = useState<GifSession | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)
  const [duplicatingSessionId, setDuplicatingSessionId] = useState<string | null>(null)

  const handleOpenSession = (session: GifSession) => {
    navigate(`/?view=gif&session=${session.id}`)
  }

  const handleCreateNew = () => {
    navigate('/?view=gif')
  }

  const handleDuplicateSession = async (session: GifSession) => {
    if (duplicatingSessionId) return

    setDuplicatingSessionId(session.id)
    try {
      await duplicateSession(session.id)
    } finally {
      setDuplicatingSessionId(null)
    }
  }

  const confirmDelete = async () => {
    if (sessionToDelete && !isDeleting) {
      setIsDeleting(true)
      try {
        const success = await deleteSession(sessionToDelete.id)
        // Only refresh if deletion failed to sync with server state
        if (!success) {
          await refreshSessions()
        }
      } finally {
        setIsDeleting(false)
        setSessionToDelete(null)
        setDeleteDialogOpen(false)
      }
    }
  }

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <Card key={i}>
            <CardContent className="p-4">
              <Skeleton className="aspect-video rounded-md mb-3" />
              <Skeleton className="h-4 w-3/4 mb-2" />
              <Skeleton className="h-3 w-1/2" />
            </CardContent>
          </Card>
        ))}
      </div>
    )
  }

  if (!sessions || sessions.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <Gift className="h-16 w-16 text-muted-foreground/50 mb-4" />
        <h3 className="text-lg font-medium mb-2">No GIF sessions yet</h3>
        <p className="text-muted-foreground mb-6 max-w-sm">
          Create your first GIF by extracting frames from a video in the Studio
        </p>
        <Button onClick={handleCreateNew} className="gap-2">
          <Plus className="h-4 w-4" />
          Create GIF
        </Button>
      </div>
    )
  }

  return (
    <>
      <div className="flex justify-end mb-4">
        <Button onClick={handleCreateNew} className="gap-2">
          <Plus className="h-4 w-4" />
          New GIF Session
        </Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {sessions.map((session) => (
          <Card 
            key={session.id}
            className={cn(
              'group cursor-pointer transition-all hover:shadow-md',
              'hover:-translate-y-0.5'
            )}
            onClick={() => handleOpenSession(session)}
          >
            <CardContent className="p-4">
              {/* Thumbnail preview */}
              <div className="aspect-video bg-muted rounded-md mb-3 overflow-hidden relative">
                {session.frames.length > 0 ? (
                  <div className="grid grid-cols-2 grid-rows-2 h-full gap-0.5">
                    {session.frames.slice(0, 4).map((frame) => (
                      <div 
                        key={frame.id} 
                        className="bg-muted-foreground/10 overflow-hidden"
                      >
                        {frame.url && (
                          <img 
                            src={frame.url} 
                            alt=""
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              // Hide broken image gracefully
                              (e.target as HTMLImageElement).style.display = 'none';
                            }}
                          />
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="flex items-center justify-center h-full">
                    <Images className="h-8 w-8 text-muted-foreground/50" />
                  </div>
                )}
                
                {/* Frame count badge */}
                <Badge 
                  variant="secondary" 
                  className="absolute bottom-2 right-2 bg-background/80 backdrop-blur-sm"
                >
                  {session.frames.length} frames
                </Badge>
              </div>
              
              {/* Session info */}
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <h4 className="font-medium truncate">{session.name}</h4>
                  <p className="text-xs text-muted-foreground">
                    Updated {new Date(session.updatedAt).toLocaleDateString()}
                  </p>
                </div>
                
                {/* Actions menu */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                    <Button variant="ghost" size="icon" className="h-8 w-8 opacity-0 group-hover:opacity-100">
                      <MoreVertical className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleOpenSession(session); }}>
                      <ExternalLink className="mr-2 h-4 w-4" />
                      Open
                    </DropdownMenuItem>
                    <DropdownMenuItem 
                      onClick={(e) => { 
                        e.stopPropagation(); 
                        const newName = prompt('Rename session:', session.name);
                        if (newName && newName !== session.name) {
                          renameSession(session.id, newName);
                        }
                      }}
                    >
                      <Pencil className="mr-2 h-4 w-4" />
                      Rename
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      disabled={duplicatingSessionId === session.id}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDuplicateSession(session);
                      }}
                    >
                      <Copy className={`mr-2 h-4 w-4 ${duplicatingSessionId === session.id ? 'animate-pulse' : ''}`} />
                      Duplicate
                    </DropdownMenuItem>
                    <DropdownMenuItem 
                      className="text-destructive"
                      onClick={(e) => { 
                        e.stopPropagation(); 
                        setSessionToDelete(session);
                        setDeleteDialogOpen(true);
                      }}
                    >
                      <Trash2 className="mr-2 h-4 w-4" />
                      Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Delete confirmation dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={(open) => !isDeleting && setDeleteDialogOpen(open)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete GIF Session?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete "{sessionToDelete?.name}" and all its frames.
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={confirmDelete} 
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground"
            >
              {isDeleting ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
